import React from 'react';
import '../styles/EnrollmentContractPreview.css';

export interface EnrollmentContractPreviewData {
  packageName: string;
  billingTerms: string;
  onlineBillingTerms: string;
  onlinePackageText: string;
  initialPaymentAmount: number;
  recurringAmount: number;
  numberOfPayments: number | null;
  paymentFrequency: string;
  memberName: string;
  siteName: string;
  siteAddress: string;
  siteCity: string;
  siteState: string;
  siteZip: string;
  sitePhone: string;
  siteLogo: string;
  memberEmail: string;
  memberPhone: string;
  contractHtml?: string | null;
}

export interface ContractFormData {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  birthDate: string;
  genderTypeId: number;
}

interface EnrollmentContractPreviewProps {
  data: EnrollmentContractPreviewData;
  formData: ContractFormData;
}

const GENDER_LABELS: Record<number, string> = { 1: 'Male', 2: 'Female', 3: 'Other' };

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T12:00:00`);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const formatPhone = (phone: string): string => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const todayFormatted = (): string => {
  const d = new Date();
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const EnrollmentContractPreview: React.FC<EnrollmentContractPreviewProps> = ({ data, formData }) => {
  // When the package has an HTML template, render it directly in a sandboxed iframe
  if (data.contractHtml) {
    return (
      <iframe
        title="Membership Agreement"
        srcDoc={data.contractHtml}
        sandbox="allow-same-origin"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
      />
    );
  }

  const termsHtml = data.onlineBillingTerms || data.billingTerms;

  const siteLocation = [
    data.siteAddress,
    [data.siteCity, data.siteState].filter(Boolean).join(', '),
    data.siteZip
  ].filter(Boolean).join(' | ');

  return (
    <div className="enrollment-contract-paper">
      {/* Header */}
      <div className="ecp-header-row">
        <div className="ecp-header-logo">
          {data.siteLogo ? (
            <img src={data.siteLogo} alt={data.siteName} />
          ) : (
            <div className="ecp-logo-placeholder">{data.siteName?.charAt(0) ?? '?'}</div>
          )}
        </div>
        <div className="ecp-header-center">
          <h1>MEMBERSHIP AGREEMENT</h1>
          <p className="ecp-gym-name">
            {data.siteName}{data.sitePhone ? ` | ${formatPhone(data.sitePhone)}` : ''}
          </p>
          {siteLocation && <p>{siteLocation}</p>}
        </div>
        <div className="ecp-header-credit">
          <strong>Credit Statement:</strong> The Federal Equal Credit Opportunity Act prohibits
          creditors from discriminating against credit applicants on the basis of sex or marital
          status. The agency that administers compliance with the law is the Federal Trade Commission,
          Equal Credit Opportunity, Washington, D.C. 20580.
        </div>
      </div>

      {/* Data Row 1: Date, Buyer, Birthdate, Gender, Package */}
      <div className="ecp-data-row">
        <div className="ecp-data-cell">
          <span className="ecp-data-value ecp-underline">{todayFormatted()}</span>
          <span className="ecp-data-label">Date</span>
        </div>
        <div className="ecp-data-cell ecp-wide">
          <span className="ecp-data-value ecp-underline">{data.memberName}</span>
          <span className="ecp-data-label">Buyer</span>
        </div>
        <div className="ecp-data-cell">
          <span className="ecp-data-value ecp-underline">
            {formData.birthDate ? formatDate(formData.birthDate) : ''}
          </span>
          <span className="ecp-data-label">Birthdate</span>
        </div>
        <div className="ecp-data-cell">
          <span className="ecp-data-value ecp-underline">
            {formData.genderTypeId ? (GENDER_LABELS[formData.genderTypeId] ?? '') : ''}
          </span>
          <span className="ecp-data-label">Gender</span>
        </div>
        <div className="ecp-data-cell ecp-wide">
          <span className="ecp-data-value ecp-underline ecp-highlight">{data.packageName}</span>
          <span className="ecp-data-label">Package</span>
        </div>
      </div>

      {/* Data Row 2: Address, City, State, Zip, Phone, Email */}
      <div className="ecp-data-row">
        <div className="ecp-data-cell ecp-wide">
          <span className="ecp-data-value ecp-underline">{formData.address}</span>
          <span className="ecp-data-label">Street Address</span>
        </div>
        <div className="ecp-data-cell">
          <span className="ecp-data-value ecp-underline">{formData.city}</span>
          <span className="ecp-data-label">City</span>
        </div>
        <div className="ecp-data-cell ecp-narrow">
          <span className="ecp-data-value ecp-underline">{formData.state}</span>
          <span className="ecp-data-label">State</span>
        </div>
        <div className="ecp-data-cell">
          <span className="ecp-data-value ecp-underline">{formData.zipCode}</span>
          <span className="ecp-data-label">Zip Code</span>
        </div>
        <div className="ecp-data-cell">
          <span className="ecp-data-value ecp-underline">{formatPhone(data.memberPhone)}</span>
          <span className="ecp-data-label">Cell Phone</span>
        </div>
        <div className="ecp-data-cell ecp-wide">
          <span className="ecp-data-value ecp-underline">{data.memberEmail}</span>
          <span className="ecp-data-label">Email Address</span>
        </div>
      </div>

      {/* Members On Contract */}
      <div className="ecp-members-section">
        <div className="ecp-section-header">Members On Contract</div>
        <div className="ecp-members-row">
          <div className="ecp-member-cell">
            <span className="ecp-member-label">Name</span>
            <span className="ecp-member-name">{data.memberName}</span>
          </div>
        </div>
      </div>

      {/* Initial Investment */}
      <div className="ecp-investment-section">
        <div className="ecp-investment-header">Initial Investment</div>
        <table className="ecp-investment-table">
          <tbody>
            <tr>
              <td className="ecp-item-number">1.</td>
              <td className="ecp-item-name">Dues:</td>
              <td className="ecp-item-amount">${data.initialPaymentAmount.toFixed(2)}</td>
            </tr>
            {data.recurringAmount > 0 && (
              <tr>
                <td className="ecp-item-number">2.</td>
                <td className="ecp-item-name">
                  {data.paymentFrequency || 'Recurring'} Payment
                  {data.numberOfPayments ? ` \u00d7 ${data.numberOfPayments}` : ''}:
                </td>
                <td className="ecp-item-amount">${data.recurringAmount.toFixed(2)}</td>
              </tr>
            )}
            <tr className="ecp-total-row">
              <td></td>
              <td className="ecp-item-name"><strong>Total Due Today:</strong></td>
              <td className="ecp-item-amount"><strong>${data.initialPaymentAmount.toFixed(2)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Terms and Conditions */}
      <div className="ecp-terms-section">
        <div className="ecp-terms-header">TERMS AND CONDITIONS</div>
        <div className="ecp-terms-content">
          {termsHtml ? (
            <div dangerouslySetInnerHTML={{ __html: termsHtml }} />
          ) : (
            <p>No terms and conditions are available for this package.</p>
          )}
        </div>
      </div>

      {data.onlinePackageText && (
        <div className="ecp-terms-section">
          <div className="ecp-terms-header">AGREEMENT DETAILS</div>
          <div className="ecp-terms-content">
            <div dangerouslySetInnerHTML={{ __html: data.onlinePackageText }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrollmentContractPreview;
